'use client'

import {
  BI_APP_ID,
  BI_CATEGORY_PAGE_VIEW,
  BI_EVENT_OPEN_PAGE,
  BI_SDK_LOADED_EVENT,
  BI_VERSION,
} from '@/constants/bi-sdk'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { getLocationHrefUrl } from '@/lib/url'
import Script from 'next/script'
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

const getTMS = () => Date.now()

const getTZ = () => new Date().getTimezoneOffset() * -1 * 60 * 1000

type LoggerOptions = {
  ev: string
  category: string
  account_id?: string
  a1?: string
  a2?: string
  a3?: string
  a4?: string
  a12?: string
}

type BISdkContextType = {
  sendBIReport: (options: LoggerOptions) => void
  isLoadedBISdk: boolean
}

const BISdkContext = createContext<BISdkContextType | null>(null)

export const BISdkProvider = ({ children }: PropsWithChildren) => {
  const [biSdkLogger, setBISdkLogger] = useState<BISdkLogger | null>(null)
  const { accountId, appConfig } = useConfigsContext()
  const { device } = useUAContext()
  const biSdkUri = appConfig?.features?.bi_sdk_uri
  const isLoadedBISdk = biSdkLogger !== null

  const sendBIReport = useCallback(
    (options: LoggerOptions) => {
      if (biSdkLogger === null) {
        console.error('BISdk is not loaded')
        return
      }

      biSdkLogger.log({
        ...options,
        bi_data_point_version: BI_VERSION,
        tms: getTMS(),
        tz: getTZ(),
        account_id: accountId,
        a0: device,
      })
    },
    [biSdkLogger, accountId, device],
  )

  const handleLoad = () => {
    console.log('✅ BISdk loaded')
    window.dispatchEvent(new CustomEvent(BI_SDK_LOADED_EVENT))
  }

  const handleReady = () => {
    console.log('🔥 BISdk ready')

    const biSdkLogger: BISdkLogger = VrBiJs.getLogger(BI_APP_ID)
    setBISdkLogger(biSdkLogger)
  }

  const handleError = (e: Error) => {
    console.error('BISdk script failed to load', e)
  }

  return (
    <BISdkContext.Provider value={{ sendBIReport, isLoadedBISdk }}>
      {children}
      {biSdkUri && (
        <Script src={biSdkUri} onLoad={handleLoad} onError={handleError} onReady={handleReady} />
      )}
    </BISdkContext.Provider>
  )
}

export const useBISdkContext = () => {
  const value = useContext(BISdkContext)

  if (value == null) {
    throw new Error('useBISdkContext cannot be used outside of BISdkProvider')
  }

  return value
}

export const useBIPageView = () => {
  const { accountId, accountType } = useConfigsContext()
  const { isLoadedBISdk, sendBIReport } = useBISdkContext()

  useEffect(() => {
    if (isLoadedBISdk) {
      sendBIReport({
        ev: BI_EVENT_OPEN_PAGE,
        category: BI_CATEGORY_PAGE_VIEW,
        account_id: accountId,
        a1: accountType,
        a4: getLocationHrefUrl(),
      })
    }
  }, [isLoadedBISdk, sendBIReport, accountId, accountType])
}
